import { Reducer, RefObject, useCallback, useEffect, useReducer } from 'react';
import { useIsTouchOnlyDevice } from './useIsTouchOnlyDevice';
import { useWindowSize } from './useWindowSize';

type SliderArrowState = {
    cursor: number;
    containerWidth: number;
    contentWidth: number;
    itemWidth: number;
    from?: 'scroll' | 'click';
    scrolledItems?: number;
};

type Actions =
    | { type: 'NEXT' }
    | { type: 'PREV' }
    | {
        type: 'GO_TO';
        cursor: number;
        from?: 'scroll' | 'click';
        scrolledItems?: number;
    }
    | {
        type: 'RESIZE';
        payload: Omit<SliderArrowState, 'cursor'>;
    };

const computeScroll = (state: SliderArrowState): number|null => {
    if (state.contentWidth > state.containerWidth) {
        return Math.min(
            getCursor(state) * state.itemWidth,
            state.contentWidth - state.containerWidth
        );
    }
    return null
};

const trunc = (value: number) => parseInt(value.toString());

const maxCursor = (state: SliderArrowState) => trunc(state.contentWidth / state.itemWidth) - getVisibleItems(state);

const getCursor = (state: SliderArrowState) => clampCursor(state.cursor, state);

const getVisibleItems = (state: SliderArrowState) =>
    trunc(state.containerWidth / state.itemWidth);

const clampCursor = (cursor: number, state: SliderArrowState) =>
    Math.max(Math.min(cursor, maxCursor(state)), 0);

const deriveState = (state: SliderArrowState) => ({
    scroll: computeScroll(state),
    leftButtonVisible: state.cursor > 0,
    rightButtonVisible: state.cursor < maxCursor(state),
    cursor: state.cursor,
    scrolledItems: state.scrolledItems,
});

const getSizes = (
    content: RefObject<HTMLElement>
): Omit<SliderArrowState, 'cursor'> => ({
    containerWidth: content.current?.clientWidth || 0,
    contentWidth: content.current?.scrollWidth || 0,
    itemWidth: content.current?.firstElementChild?.clientWidth || 0,
});

export const useScrollNavigator = (content: RefObject<HTMLElement>) => {
    const [state, dispatch] = useReducer<Reducer<SliderArrowState, Actions>>(
        (state, action) => {
            const cursor = getCursor(state);

            switch (action.type) {
                case 'PREV':
                    return {
                        ...state,
                        cursor: clampCursor(state.cursor - 1, state),
                        from: 'click',
                    };
                case 'NEXT':
                    if (cursor === maxCursor(state)) return state;

                    return {
                        ...state,
                        cursor: clampCursor(state.cursor + 1, state),
                        from: 'click',
                    };
                case 'GO_TO':
                    return {
                        ...state,
                        cursor: clampCursor(action.cursor, state),
                        from: action.from,
                        scrolledItems: action.scrolledItems,
                    };
                case 'RESIZE':
                    return {
                        ...state,
                        ...action.payload,
                    };
            }
        },
        {
            cursor: 0,
            containerWidth: 1,
            contentWidth: 1,
            itemWidth: 1,
        }
    );

    const handleResize = useCallback(() => {
        dispatch({
            type: 'RESIZE',
            payload: getSizes(content),
        });
    }, [dispatch, content]);

    const size = useWindowSize();

    useEffect(() => {
        handleResize();
    }, [size, handleResize]);

    const next = () => dispatch({ type: 'NEXT' });
    const prev = () => dispatch({ type: 'PREV' });
    const goTo = (cursor: number) =>
        dispatch({ type: 'GO_TO', cursor, from: 'click' });

    const computedState = deriveState(state);

    const isTouchOnlyDevice = useIsTouchOnlyDevice(true);

    const { cursor, itemWidth, from, scrolledItems } = state;

    useEffect(() => {
        const el = content.current;
        if(!el) return

        function handleScroll() {
            const { scrollLeft } = el as HTMLElement ;

            const realCursor = scrollLeft / itemWidth;
            let cursorValue = Math.abs(realCursor - cursor);

            //Patch per Chrome su alcuni dispositivi Android
            //sui quali scrollLeft non viene arrotondato ad intero
            //https://support.google.com/chrome/thread/4220583
            if (
                (from === 'scroll' ||
                    (typeof from === 'undefined' && cursor === 0)) &&
                scrollLeft % 1 !== 0 &&
                cursorValue >= 0.99 &&
                cursorValue < 1
            ) {
                cursorValue = 1;
            }

            //Se la larghezza dell'elemento non è un intero
            //lo scroll si porta appresso un errore
            //Purtroppo la scrollPosition è sempre intera e quindi
            //possiamo solo essere più tolleranti con i check
            if (cursorValue >= 1) {
                dispatch({
                    type: 'GO_TO',
                    cursor: Math.round(realCursor),
                    from: 'scroll',
                    scrolledItems: cursorValue,
                });
            }
        }

            el.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            el.removeEventListener('scroll', handleScroll, {
                passive: true,
            } as any);
        };
    }, [cursor, itemWidth, content, dispatch]);

    useEffect(() => {
        if (from !== 'click') return;

        const el = content.current;
        const pos = itemWidth * cursor;

        if(el){
            el.scrollLeft = pos;
        }
    }, [from, cursor, itemWidth, content]);

    return {
        next,
        prev,
        goTo,
        rightButtonVisible:
            !isTouchOnlyDevice && computedState.rightButtonVisible,
        leftButtonVisible:
            !isTouchOnlyDevice && computedState.leftButtonVisible,
        cursor: computedState.cursor,
        scroll: computedState.scroll,
        noPrevItem: !computedState.leftButtonVisible,
        noNextItem: !computedState.rightButtonVisible,
        from,
        scrolledItems,
    };
};
